<script lang="ts">
  import { targetLatencyMs, draftLatencyMs, k, acceptanceProb, computation } from './speedup-store';

  function formatSpeedup(value: number): string {
    return value.toFixed(2) + '×';
  }

  function formatPercent(value: number): string {
    return Math.round(value * 100) + '%';
  }

  // Slider binding helpers - sliders work in smaller units, stores hold ms
  $: targetSlider = $targetLatencyMs / 10;
  $: draftSlider = $draftLatencyMs / 10;

  function setTarget(value: number) {
    targetLatencyMs.set(value * 10);
  }

  function setDraft(value: number) {
    draftLatencyMs.set(value * 10);
  }
</script>

<div class="controls">
  <div class="speedup-row">
    <div class="legend">
      <span class="legend-item"><span class="legend-dot sequential"></span>Sequential</span>
      <span class="legend-item"><span class="legend-dot speculative"></span>Speculative Decoding</span>
    </div>
    <div class="speedup-display">
      <span class="speedup-label">Speedup</span>
      <span class="speedup-value">{formatSpeedup($computation.speedup)}</span>
    </div>
  </div>

  <div class="sliders">
    <div class="slider-row">
      <label for="k-slider" class="slider-label">K</label>
      <input id="k-slider" type="range" min="1" max="10" step="1" bind:value={$k} class="slider" />
      <span class="slider-value">{$k}</span>
    </div>

    <div class="slider-row">
      <label for="accept-slider" class="slider-label">Accept</label>
      <input id="accept-slider" type="range" min="0" max="1" step="0.05" bind:value={$acceptanceProb} class="slider slider-accept" />
      <span class="slider-value">{formatPercent($acceptanceProb)}</span>
    </div>

    <div class="slider-row">
      <label for="target-slider" class="slider-label">Target</label>
      <input id="target-slider" type="range" min="20" max="200" step="5" value={targetSlider} on:input={(e) => setTarget(+e.currentTarget.value)} class="slider slider-target" />
      <span class="slider-value">{$targetLatencyMs}ms</span>
    </div>

    <div class="slider-row">
      <label for="draft-slider" class="slider-label">Draft</label>
      <input id="draft-slider" type="range" min="1" max="50" step="1" value={draftSlider} on:input={(e) => setDraft(+e.currentTarget.value)} class="slider slider-draft" />
      <span class="slider-value">{$draftLatencyMs}ms</span>
    </div>
  </div>
</div>

<style>
  .controls {
    padding: 0.75rem 1rem;
    background: #fafafa;
    border-top: 1px solid #eee;
  }

  .speedup-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #eee;
  }

  .legend {
    display: flex;
    gap: 0.75rem;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.75rem;
    color: #666;
  }

  .legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 2px;
  }

  .legend-dot.sequential {
    background: #4a90d9;
  }

  .legend-dot.speculative {
    background: #8b5cf6;
  }

  .speedup-display {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0;
  }

  .speedup-label {
    font-size: 0.625rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #888;
  }

  .speedup-value {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 1.125rem;
    font-weight: 700;
    line-height: 1.1;
    color: #3fb87a;
  }

  .sliders {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .slider-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .slider-label {
    font-size: 0.75rem;
    font-weight: 500;
    color: #555;
    width: 3rem;
    flex-shrink: 0;
  }

  .slider {
    flex: 1;
    -webkit-appearance: none;
    appearance: none;
    height: 5px;
    border-radius: 3px;
    background: #ddd;
    outline: none;
    cursor: pointer;
  }

  .slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #555;
    cursor: pointer;
    border: 2px solid #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }

  .slider::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #555;
    cursor: pointer;
    border: 2px solid #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }

  .slider-target::-webkit-slider-thumb { background: #2a4a6d; }
  .slider-target::-moz-range-thumb { background: #2a4a6d; }
  .slider-draft::-webkit-slider-thumb { background: #8b5cf6; }
  .slider-draft::-moz-range-thumb { background: #8b5cf6; }
  .slider-accept::-webkit-slider-thumb { background: #3fb87a; }
  .slider-accept::-moz-range-thumb { background: #3fb87a; }

  .slider-value {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.6875rem;
    color: #333;
    background: #fff;
    padding: 0.125rem 0.375rem;
    border-radius: 3px;
    border: 1px solid #e0e0e0;
    min-width: 2.5rem;
    text-align: center;
  }

</style>
